import { events } from "@planzone/events";
import { Boundaries } from "@planzone/core";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = [];

function handleRequest(
  state: State,
  project: Boundaries["retrieveProjectReports"]["request"],
): State {
  return [
    ...state,
    { request: setRequestStateToPending(), project, reports: [] },
  ];
}

function handleSuccess(
  state: State,
  { project, reports }: Boundaries["retrieveProjectReports"]["success"],
): State {
  return state.map((res) =>
    res.project.uuid === project.uuid
      ? { ...res, request: setRequestStateToSuccess(), project, reports }
      : res,
  );
}

function handleFailure(
  state: State,
  project: Boundaries["retrieveProjectReports"]["failure"],
): State {
  return state.map((res) =>
    res.project.uuid === project.uuid
      ? { ...res, request: setRequestStateToFailure() }
      : res,
  );
}

function handleSubmitProjectReport(
  state: State,
  { project, report }: Boundaries["submitProjectReport"]["success"],
): State {
  return state.map((res) =>
    res.project.uuid === project.uuid
      ? { ...res, reports: [report, ...res.reports] }
      : res,
  );
}

function handleRemoveReport(
  state: State,
  { project, report }: Boundaries["removeProjectReport"]["success"],
): State {
  return state.map((res) =>
    res.project.uuid === project.uuid
      ? {
          ...res,
          reports: res.reports.filter(({ uuid }) => uuid !== report.uuid),
        }
      : res,
  );
}

function handleEditReport(
  state: State,
  { project, report }: Boundaries["editProjectReport"]["success"],
): State {
  return state.map((res) =>
    res.project.uuid === project.uuid
      ? {
          ...res,
          reports: res.reports.map((r) =>
            r.uuid === report.uuid ? report : r,
          ),
        }
      : res,
  );
}

const reduceState = resetState(
  initialState,
  createStateReducer(initialState, {
    [events.retrieveProjectReportsRequested]: handleRequest,
    [events.retrieveProjectReportsSucceeded]: handleSuccess,
    [events.retrieveProjectReportsFailed]: handleFailure,
    [events.submitProjectReportSucceeded]: handleSubmitProjectReport,
    [events.removeProjectReportSucceeded]: handleRemoveReport,
    [events.editProjectReportSucceeded]: handleEditReport,
  }),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
